import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getProfile, getSentReferrals, getReceivedReferrals, providerRespond, editOwnReferral, deleteOwnReferral, getServiceCatalog, getMyStats, getUpcomingEvents, getMyAttendance, checkinEvent, cancelCheckin, getMyInvitados, createInvitado, linkInvitadoToEvent } from '../api/api';
import { StatTile, StatusBarChart, StatusBadge, ReferralDetailModal, statusLabel, folio, SkeletonPage, Avatar, UpcomingEventsPanel, TrendLine, CheckinModal } from '../components/Charts';
import { uiAlert, uiConfirm, uiMoneyPrompt, uiForm } from '../components/Modal';
import { uiToastSuccess, uiToastError } from '../components/Toast';

const money = (n) => `$${parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// TOUR DE BIENVENIDA — sale en el 1er y 2do inicio de sesión (login_count <= 2, contado en
// server.js) y ya no vuelve a aparecer después. Se decidió que fueran dos en vez de uno solo
// porque la primera sesión normalmente se va completa en llenar Mi Perfil y el primer servicio
// (paso obligatorio), así que el usuario apenas ve el dashboard real hasta la 2da vez que entra.
// El orden de pasos sigue el orden visual real de los módulos (ver DASHBOARD_ORDER abajo).
const TOUR_STEP_DEFS = {
  events: { element: '#tour-events', popover: { title: 'Próximos eventos', description: 'Aquí ves la fecha del próximo Coffee HUB y la Junta Mensual. Regístrate con un click cuando vayas a asistir.', side: 'bottom', align: 'start' } },
  daterange: { element: '#tour-daterange', popover: { title: 'Filtra por fechas', description: 'Elige un rango de fechas para ver tus referencias y comisiones de ese periodo.', side: 'bottom', align: 'start' } },
  kpis: { element: '#tour-kpis', popover: { title: 'Tus números', description: 'Referencias enviadas/recibidas, valor cerrado, lo que te deben (por cobrar/recibido) y lo que tú debes si cerraste un trato como proveedor (por pagar/pagado).', side: 'bottom', align: 'start' } },
  trend: { element: '#tour-trend', popover: { title: 'Tendencia', description: 'Tus referencias a lo largo del tiempo, según el rango que elijas arriba.', side: 'top', align: 'start' } },
  statuses: { element: '#tour-statuses', popover: { title: 'Por estado', description: 'Compara lo que enviaste contra lo que recibiste, según el estado de cada referencia.', side: 'top', align: 'start' } },
  tabs: { element: '#tour-tabs', popover: { title: 'Tus referencias', description: 'Aquí puedes ver el detalle de todo lo que enviaste y recibiste, y responder cuando te toque.', side: 'top', align: 'start' } },
};

// Orden fijo de los módulos del dashboard. Antes existían 3 "vistas" elegibles
// (Balanceado / Enfoque en comisiones / Enfoque en actividad) — se quitaron: nadie las
// usaba para otra cosa más que confundir, y complicaban el tour y el layout sin aportar nada.
const DASHBOARD_ORDER = ['events', 'kpis', 'trend', 'statuses'];

function tourSteps() {
  const ids = [];
  DASHBOARD_ORDER.forEach(block => ids.push(...(block === 'kpis' ? ['daterange', 'kpis'] : [block])));
  ids.push('tabs');
  return ids.map(id => TOUR_STEP_DEFS[id]).filter(Boolean);
}

function startDashboardTour() {
  const steps = tourSteps().filter(s => document.querySelector(s.element));
  if (steps.length === 0) return;
  const tour = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayClickBehavior: 'close',
    stagePadding: 6,
    stageRadius: 8,
    nextBtnText: 'Siguiente',
    prevBtnText: 'Anterior',
    doneBtnText: 'Listo',
    closeBtnText: 'Saltar tour',
    showButtons: ['next', 'previous', 'close'],
    steps,
  });
  tour.drive();
}

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState('sent');
  const [sent, setSent] = useState([]);
  // Catálogo completo de servicios de la red — solo se usa para armar el selector de
  // "Empresa — Servicio" al editar una referencia ya enviada (ver editSentReferral).
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [received, setReceived] = useState([]);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [myInvitados, setMyInvitados] = useState([]);
  const [checkinEventTarget, setCheckinEventTarget] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [sentStatusFilter, setSentStatusFilter] = useState(null);
  const [receivedStatusFilter, setReceivedStatusFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    getProfile()
      .then(res => {
        setProfile(res.data);
        setLoading(false);
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  useEffect(() => {
    if (profile && profile.status === 'active') {
      loadSent();
      loadReceived();
      loadStats();
      loadEvents();
      loadServiceCatalog();
    }
  }, [profile]);

  // Polling — refresca lo que ve el usuario cada 25s sin que tenga que darle F5. No es
  // instantáneo (para eso haría falta WebSockets), pero para este hub es más que suficiente
  // y no agrega infraestructura nueva. La campanita de notificaciones ya hacía algo parecido.
  const POLL_MS = 25000;
  const dateRangeRef = useRef({ dateFrom: '', dateTo: '' });
  useEffect(() => { dateRangeRef.current = { dateFrom, dateTo }; }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!profile || profile.status !== 'active') return;
    const interval = setInterval(() => {
      loadSent();
      loadReceived();
      loadEvents();
      const { dateFrom: f, dateTo: t } = dateRangeRef.current;
      loadStats(f || undefined, t || undefined);
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tour de bienvenida — ver nota arriba, en TOUR_STEP_DEFS. Solo en el 1er y 2do login.
  useEffect(() => {
    if (!profile || profile.status !== 'active' || loading) return;
    if ((profile.login_count || 0) > 2) return;
    const t = setTimeout(() => startDashboardTour(), 400); // espera a que el resumen ya esté pintado
    return () => clearTimeout(t);
  }, [profile, loading]);

  // Los KPIs de arriba (tiles y gráficas de estado) solo cambian el tab/filtro de la
  // lista de referencias que vive más abajo en la misma página — si la persona ya está
  // scrolleada arriba, el cambio de tab pasa "invisible" porque la lista no entra en
  // pantalla. Este helper hace scroll a la sección de tabs cada vez que se usa uno de
  // esos atajos, para que el click sí se sienta como que "te lleva" a algo.
  const goToReferralsTab = (tabName, statusFilter = null) => {
    setTab(tabName);
    if (tabName === 'sent') setSentStatusFilter(statusFilter);
    else setReceivedStatusFilter(statusFilter);
    requestAnimationFrame(() => {
      document.getElementById('tour-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const loadStats = (from, to) => getMyStats(from, to).then(res => setStats(res.data)).catch(() => {});

  // Recarga el resumen (tiles + gráfica) cuando el usuario elige un rango de fechas
  useEffect(() => {
    if (profile && profile.status === 'active' && dateFrom && dateTo) loadStats(dateFrom, dateTo);
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSent = () => getSentReferrals().then(res => setSent(res.data)).catch(() => {});
  const loadServiceCatalog = () => getServiceCatalog().then(res => setServiceCatalog(res.data)).catch(() => {});

  // Editar/eliminar una referencia propia solo es posible mientras la empresa no haya
  // respondido (ver candado igual en el backend, PATCH/DELETE /api/referrals/:id) — una vez
  // que responde, ya pudo generar comisión y el cambio le corresponde a administración.
  const editSentReferral = async (r) => {
    // Lista única de empresas (mismo patrón que NuevaReferida.js), ordenada por nombre.
    const providers = Array.from(
      new Map(serviceCatalog.map(s => [s.provider_id, { provider_id: s.provider_id, business_name: s.business_name }])).values()
    ).sort((a, b) => a.business_name.localeCompare(b.business_name));

    const currentService = serviceCatalog.find(s => String(s.service_id) === String(r.service_id));
    const initialProviderId = currentService ? String(currentService.provider_id) : String(r.provider_id || '');

    const values = await uiForm('Editar referencia', [
      {
        name: 'provider_id', label: 'Empresa', type: 'select', value: initialProviderId,
        options: providers.map(p => ({ value: String(p.provider_id), label: p.business_name })),
      },
      {
        // Opciones dependientes de la empresa elegida arriba — se recalculan solas en cuanto
        // cambias "provider_id" (ver setFormField en Modal.js). Si la empresa cambia, el
        // servicio se limpia al primero de la lista nueva en vez de dejar uno de otra empresa.
        name: 'service_id', label: 'Servicio', type: 'select', value: String(r.service_id),
        options: (values) => serviceCatalog
          .filter(s => String(s.provider_id) === String(values.provider_id))
          .map(s => ({ value: String(s.service_id), label: s.service_name })),
      },
      { name: 'prospect_name', label: 'Nombre del prospecto', type: 'text', value: r.prospect_name },
      { name: 'prospect_phone', label: 'Teléfono del prospecto', type: 'text', value: r.prospect_phone || '' },
      { name: 'notes', label: 'Comentarios', type: 'text', value: r.notes || '' },
    ]);
    if (!values) return;
    try {
      await editOwnReferral(r.id, { ...values, provider_id: undefined });
      uiToastSuccess('Referencia actualizada.');
      loadSent();
    } catch (err) {
      uiToastError(err.response?.data?.error || 'No se pudo editar la referencia');
    }
  };

  const deleteSentReferral = async (r) => {
    if (!(await uiConfirm(`¿Eliminar la referencia de ${r.prospect_name}? No se puede deshacer.`))) return;
    try {
      await deleteOwnReferral(r.id);
      uiToastSuccess('Referencia eliminada.');
      loadSent();
    } catch (err) {
      uiToastError(err.response?.data?.error || 'No se pudo eliminar la referencia');
    }
  };
  const loadReceived = () => getReceivedReferrals().then(res => setReceived(res.data)).catch(() => {});
  const loadEvents = () => {
    getUpcomingEvents().then(res => setEvents(res.data)).catch(() => {});
    getMyAttendance().then(res => setAttendance(res.data)).catch(() => {});
    getMyInvitados().then(res => setMyInvitados(res.data)).catch(() => {});
  };

  // Confirma asistencia y, si aplica, registra o vincula al invitado que se lleva a ese evento.
  const handleConfirmCheckin = async ({ invitadoId, newGuest }) => {
    try {
      await checkinEvent(checkinEventTarget.id);
      if (invitadoId) {
        await linkInvitadoToEvent(invitadoId, checkinEventTarget.id);
      } else if (newGuest) {
        await createInvitado({ ...newGuest, event_id: checkinEventTarget.id });
      }
      uiToastSuccess('Asistencia registrada.');
      setCheckinEventTarget(null);
      loadEvents();
    } catch (err) {
      uiToastError('Error registrando asistencia: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelCheckin = async (ev) => {
    if (!(await uiConfirm('¿Cancelar tu asistencia a este evento?'))) return;
    try {
      await cancelCheckin(ev.id);
      uiToastSuccess('Asistencia cancelada.');
      loadEvents();
    } catch (err) {
      uiToastError('Error cancelando asistencia: ' + (err.response?.data?.error || err.message));
    }
  };

  // Si se llegó desde una notificación (con un referral_id en el state de navegación),
  // busca esa referencia en lo ya cargado y abre su ventana de detalle directamente.
  useEffect(() => {
    const targetId = location.state?.openReferralId;
    if (!targetId) return;
    const found = sent.find(r => r.id === targetId) || received.find(r => r.id === targetId);
    if (found) setSelectedReferral(found);
  }, [location.state, sent, received]);

  const handleProviderResponse = async (id, outcome) => {
    let deal_amount = null;
    if (outcome === 'deal') {
      const val = await uiMoneyPrompt('¿Monto del trato cerrado?');
      if (!val) return;
      deal_amount = parseFloat(val.replace(/,/g, '').replace(/\$/g, '').trim());
      if (isNaN(deal_amount) || deal_amount <= 0) {
        await uiAlert('Monto inválido. Escribe solo números, por ejemplo: 26000');
        return;
      }
    } else {
      if (!(await uiConfirm('¿Confirmas que no se cerró negocio con este prospecto?'))) return;
    }
    try {
      await providerRespond(id, { outcome, deal_amount });
      uiToastSuccess(outcome === 'deal' ? 'Negocio reportado.' : 'Marcado como sin negocio.');
      loadReceived();
    } catch (err) {
      uiToastError('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return <SkeletonPage />;
  if (!profile) return null;

  if (profile.status !== 'active') {
    return (
      <div className="page-narrow">
        <div className="state-message">
          <h2>Cuenta pendiente de aprobación</h2>
          <p>Mientras esperas, puedes completar tu perfil y registrar tus servicios en "Mi Perfil".</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Bienvenido, {profile.name}</h1>
      <p className="page-subtitle">Aquí puedes ver y gestionar tus referencias.</p>

      <button onClick={() => navigate('/nueva-referida')} className="btn btn-primary" style={{ marginBottom: '28px' }}>
        + Nueva Referencia
      </button>

      {(() => {
        const blocks = {
          events: (
            <div id="tour-events" key="events">
              <UpcomingEventsPanel events={events} attendance={attendance} onOpenCheckin={setCheckinEventTarget} onCancelCheckin={handleCancelCheckin} />
            </div>
          ),
          kpis: stats && (
            <div key="kpis">
              <div id="tour-daterange" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '18px', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ marginBottom: 0, flex: '0 1 160px', minWidth: '140px' }}>
                  <label className="form-label">Desde</label>
                  <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0, flex: '0 1 160px', minWidth: '140px' }}>
                  <label className="form-label">Hasta</label>
                  <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                {(dateFrom || dateTo) && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); loadStats(); }}>
                    Quitar rango
                  </button>
                )}
              </div>

              <div id="tour-kpis">
                <div className="kpi-row">
                  <StatTile label="Referencias enviadas" value={sent.length} onClick={() => goToReferralsTab('sent')} />
                  <StatTile label="Referencias recibidas" value={received.length} onClick={() => goToReferralsTab('received')} />
                  <StatTile
                    label="Valor cerrado (tuyo)"
                    value={money(stats.total_closed_value)}
                    onClick={() => goToReferralsTab('received', 'confirmed_deal')}
                  />
                </div>

                <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '18px 0 10px' }}>
                  Como quien refiere
                </h3>
                <div className="kpi-row">
                  <StatTile label="Comisiones por cobrar" value={money(stats.commissions_pending_mine)} />
                  <StatTile label="Comisiones recibidas" value={money(stats.commissions_paid_mine)} />
                </div>

                {received.length > 0 && (
                  <>
                    <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '18px 0 10px' }}>
                      Como proveedor (te refirieron a ti)
                    </h3>
                    <div className="kpi-row">
                      <StatTile label="Comisiones por pagar" value={money(stats.commissions_payable_pending)} />
                      <StatTile label="Comisiones ya pagadas (tuyas)" value={money(stats.commissions_payable_paid)} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ),
          trend: stats && (
            <div id="tour-trend" key="trend">
              <TrendLine
                title="Tus referencias en el tiempo"
                subtitle={dateFrom && dateTo ? 'Rango seleccionado' : 'Últimas 8 semanas'}
                data={stats.referrals_weekly}
              />
            </div>
          ),
          statuses: stats && (
            <div id="tour-statuses" className="two-col-grid" key="statuses">
              <StatusBarChart
                title="Lo que enviaste"
                subtitle="Por estado — haz click para ver esas referencias"
                data={stats.sent_by_status}
                onSelect={(status) => goToReferralsTab('sent', status)}
              />
              <StatusBarChart
                title="Lo que recibiste"
                subtitle="Por estado — haz click para ver esas referencias"
                data={stats.received_by_status}
                onSelect={(status) => goToReferralsTab('received', status)}
              />
            </div>
          ),
        };

        return DASHBOARD_ORDER.map(key => blocks[key]).filter(Boolean);
      })()}

      <div id="tour-tabs" className="tabs">
        <button className={`tab ${tab === 'sent' ? 'active' : ''}`} onClick={() => setTab('sent')}>
          Referencias que envié
        </button>
        <button className={`tab ${tab === 'received' ? 'active' : ''}`} onClick={() => setTab('received')}>
          Referencias que recibí
        </button>
      </div>

      {tab === 'sent' && (
        <div>
          {sentStatusFilter && (
            <div className="filter-chip">
              Filtrando: {statusLabel(sentStatusFilter)}
              <button onClick={() => setSentStatusFilter(null)} title="Quitar filtro">✕</button>
            </div>
          )}
          <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Empresa</th>
                <th>Servicio</th>
                <th>Prospecto</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {sent.filter(r => !sentStatusFilter || r.status === sentStatusFilter).map(r => (
                <tr key={r.id}>
                  <td>
                    <button className="link-button" onClick={() => setSelectedReferral(r)} title="Ver toda la información">
                      {folio(r.id)}
                    </button>
                  </td>
                  <td><span className="avatar-with-label"><Avatar src={r.provider_avatar_url} seed={r.provider_business_name} size={22} />{r.provider_business_name}</span></td>
                  <td>{r.service_name}</td>
                  <td>{r.prospect_name}</td>
                  <td><StatusBadge status={r.status} businessName={r.provider_business_name} small /></td>
                  <td>
                    {r.status === 'pending_provider_response' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        <button onClick={() => editSentReferral(r)} className="btn btn-secondary btn-sm">Editar</button>
                        <button onClick={() => deleteSentReferral(r)} className="btn btn-danger btn-sm">Eliminar</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: 'var(--neutral-500)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sent.length === 0 && <div className="table-empty">Aún no has enviado referencias</div>}
          </div>
        </div>
      )}

      {tab === 'received' && (
        <div>
          {receivedStatusFilter && (
            <div className="filter-chip">
              Filtrando: {statusLabel(receivedStatusFilter)}
              <button onClick={() => setReceivedStatusFilter(null)} title="Quitar filtro">✕</button>
            </div>
          )}
          <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Referido por</th>
                <th>Servicio</th>
                <th>Prospecto</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {received.filter(r => !receivedStatusFilter || r.status === receivedStatusFilter).map(r => (
                <tr key={r.id}>
                  <td>
                    <button className="link-button" onClick={() => setSelectedReferral(r)} title="Ver toda la información">
                      {folio(r.id)}
                    </button>
                  </td>
                  <td><span className="avatar-with-label"><Avatar src={r.referrer_avatar_url} seed={r.referrer_name} size={22} />{r.referrer_name}</span></td>
                  <td>{r.service_name}</td>
                  <td>{r.prospect_name} {r.prospect_phone ? `(${r.prospect_phone})` : ''}</td>
                  <td><StatusBadge status={r.status} small /></td>
                  <td>
                    {r.status === 'pending_provider_response' && (
                      <>
                        <button onClick={() => handleProviderResponse(r.id, 'deal')} className="btn btn-success btn-sm" style={{ marginRight: '6px' }}>Sí hubo negocio</button>
                        <button onClick={() => handleProviderResponse(r.id, 'no_deal')} className="btn btn-secondary btn-sm">No hubo negocio</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {received.length === 0 && <div className="table-empty">Aún no has recibido referencias</div>}
          </div>
        </div>
      )}

      <ReferralDetailModal referral={selectedReferral} onClose={() => setSelectedReferral(null)} />
      <CheckinModal
        event={checkinEventTarget}
        myInvitados={myInvitados}
        onClose={() => setCheckinEventTarget(null)}
        onConfirm={handleConfirmCheckin}
      />
    </div>
  );
}

export default Dashboard;
